/**
 * Paper Collaboration Workspace
 * 
 * Full-featured UI for scientific paper collaboration with authority/signer messaging
 * Supports workspace manifests, bundle assembly, linkage validation, receipts, and merge engine
 */

import React, { useState, useEffect } from 'react';
import { FileText, GitBranch, Shield, Users, MessageSquare, CheckCircle, Clock, ChevronRight, Plus } from 'lucide-react';
import { Card } from './components/Card';
import { Badge } from './components/Badge';
import { BackgroundLayout } from './components/BackgroundLayout';
import { BrandTitle } from './components/BrandTitle';
import { LoadingSpinner } from './components/LoadingSpinner';
import type { 
  PaperWorkspaceManifest,
  RevisionReceipt,
} from '../modules/paper';

interface PaperCollaborationWorkspaceProps {
  user: any;
  onNavigateBack: () => void;
}

export const PaperCollaborationWorkspace: React.FC<PaperCollaborationWorkspaceProps> = ({
  user,
  onNavigateBack,
}) => {
  const [activeTab, setActiveTab] = useState<'manifest' | 'bundle' | 'linkage' | 'receipts' | 'authority' | 'merge'>('manifest');
  const [loading, setLoading] = useState(true);

  // Mock data - in production this would come from the paper module
  const [manifest, setManifest] = useState<PaperWorkspaceManifest | null>(null);
  const [receipts, setReceipts] = useState<RevisionReceipt[]>([]);

  useEffect(() => {
    // In production, load actual data from paper module
    setLoading(false);
    
    // Mock data for demonstration matching the actual types
    setManifest({
      manifestVersion: '1.0.0',
      paper: {
        paperId: 'paper-001',
        title: 'Deterministic Governance in Multi-Agent Systems',
        description: 'A framework for deterministic governance in multi-agent systems',
        authors: ['Dr. Alice Chen', 'Dr. Bob Smith'],
        version: '1.2.0',
        status: 'in-review',
        createdAt: '2026-04-01T00:00:00Z',
        modifiedAt: '2026-04-07T00:00:00Z',
      },
      sections: [
        { sectionId: 'abstract', title: 'Abstract', order: 1, contentRef: '/abstract.md', type: 'narrative' },
        { sectionId: 'introduction', title: 'Introduction', order: 2, contentRef: '/intro.md', type: 'narrative' },
        { sectionId: 'methods', title: 'Methods', order: 3, contentRef: '/methods.md', type: 'methods' },
        { sectionId: 'results', title: 'Results', order: 4, contentRef: '/results.md', type: 'results' },
      ],
      claims: [
        { claimId: 'claim-1', statement: 'Deterministic governance reduces ambiguity', sectionId: 'introduction', type: 'hypothesis', sourceIds: ['source-1'], confidence: 'high' },
        { claimId: 'claim-2', statement: 'Multi-agent coordination requires explicit authority', sectionId: 'results', type: 'finding', sourceIds: ['source-1'], confidence: 'high' },
      ],
      sources: [
        { sourceId: 'source-1', type: 'dataset', title: 'OpenAI Agent Dataset v2', reference: 'doi:10.1234/dataset', format: 'csv' },
      ],
      revision: {
        revision: 2,
        history: [
          { revision: 2, description: 'Added methods section', author: 'Dr. Alice Chen', timestamp: '2026-04-07T00:00:00Z', changedSections: ['methods'], changedClaims: [] },
          { revision: 1, description: 'Initial draft', author: 'Dr. Bob Smith', timestamp: '2026-04-06T00:00:00Z', changedSections: ['abstract', 'introduction'], changedClaims: ['claim-1'] },
        ],
      },
      assembly: {
        requiredSections: ['abstract', 'introduction', 'methods', 'results'],
        sectionOrder: { before: ['references'] },
        validation: { requireClaimSources: true, requireSourceAccessibility: true },
      },
      workspace: {
        workspaceId: 'workspace-001',
        root: '/papers/paper-001',
      },
    });

    setReceipts([
      {
        receiptVersion: '1.0.0',
        receiptId: 'receipt-001',
        paperId: 'paper-001',
        paperVersion: '1.2.0',
        revision: 2,
        manifestHash: 'sha256:abc123...',
        revisionMetadata: {
          description: 'Added methods section',
          author: 'Dr. Alice Chen',
          timestamp: '2026-04-07T00:00:00Z',
          changedSections: ['methods'],
          changedClaims: [],
        },
        createdAt: '2026-04-07T00:00:00Z',
        createdBy: 'user-001',
        signature: {
          algorithm: 'ed25519',
          signature: '0xabc123...',
          publicKey: '0xdef456...',
          keyId: 'key-001',
        },
      },
    ]);
  }, []);

  const tabs = [
    { id: 'manifest', label: 'Workspace Manifest', icon: FileText },
    { id: 'bundle', label: 'Bundle Assembly', icon: FileText },
    { id: 'linkage', label: 'Linkage Analysis', icon: GitBranch },
    { id: 'receipts', label: 'Authority Receipts', icon: Shield },
    { id: 'authority', label: 'Authority Registry', icon: Users },
    { id: 'merge', label: 'Merge Engine', icon: MessageSquare },
  ] as const;

  if (loading) {
    return (
      <BackgroundLayout>
        <LoadingSpinner 
          size="md" 
          message="Loading Paper Collaboration Workspace..."
        />
      </BackgroundLayout>
    );
  }

  return (
    <BackgroundLayout>
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4 bg-[#0a0a0f]/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onNavigateBack}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ← Back
            </button>
            <BrandTitle>
              Paper Collaboration Workspace
            </BrandTitle>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">{user.email}</span>
            <Badge status="info" />
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/10 px-6 bg-[#0a0a0f]/30">
        <div className="max-w-7xl mx-auto flex gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#00d4aa] border-b-2 border-[#00d4aa]'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {activeTab === 'manifest' && <ManifestView manifest={manifest} />}
        {activeTab === 'bundle' && <BundleView manifest={manifest} />}
        {activeTab === 'linkage' && <LinkageView manifest={manifest} />}
        {activeTab === 'receipts' && <ReceiptsView receipts={receipts} />}
        {activeTab === 'authority' && <AuthorityView />}
        {activeTab === 'merge' && <MergeView />}
      </main>
    </BackgroundLayout>
  );
};

// Tab Components
function ManifestView({ manifest }: { manifest: PaperWorkspaceManifest | null }) {
  if (!manifest) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-400">No workspace manifest loaded</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Paper Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-400">Title</label>
            <p className="text-white font-medium">{manifest.paper.title}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">Version</label>
            <p className="text-white font-medium">{manifest.paper.version}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">Authors</label>
            <p className="text-white font-medium">{manifest.paper.authors.join(', ')}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">Paper ID</label>
            <p className="text-white font-mono text-sm">{manifest.paper.paperId}</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">Status</label>
            <Badge status={manifest.paper.status} />
          </div>
          <div>
            <label className="text-sm text-gray-400">Revision</label>
            <p className="text-white font-medium">v{manifest.paper.version}</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Sections</h2>
        <div className="space-y-2">
          {manifest.sections.map((section) => (
            <div key={section.sectionId} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <ChevronRight className="w-4 h-4 text-gray-500" />
                <span className="text-white">{section.title}</span>
              </div>
              <span className="text-xs text-gray-400">{section.type}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Claims</h2>
        <div className="space-y-2">
          {manifest.claims.map((claim) => (
            <div key={claim.claimId} className="p-3 bg-white/5 rounded-lg">
              <p className="text-white mb-2">{claim.statement}</p>
              <div className="flex items-center gap-2">
                <Badge status={claim.confidence} />
                <span className="text-xs text-gray-400">{claim.type}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Revision Chain</h2>
        <div className="space-y-3">
          {manifest.revision.history.map((rev) => (
            <div key={rev.revision} className="p-4 bg-white/5 rounded-lg border-l-4 border-[#00d4aa]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Revision {rev.revision}</span>
                <span className="text-sm text-gray-400">{rev.author}</span>
              </div>
              <p className="text-sm text-gray-300">{rev.description}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                {new Date(rev.timestamp).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function BundleView({ manifest }: { manifest: PaperWorkspaceManifest | null }) {
  const [bundleStatus, setBundleStatus] = useState<any>(null);

  const handleAssembleBundle = () => {
    if (!manifest) return;
    // Mock assembly result - in production this would call assembleBundle
    setBundleStatus({
      success: true,
      bundleId: `bundle-${manifest.paper.paperId}-${Date.now()}`,
      sections: manifest.sections.length,
      claims: manifest.claims.length,
      sources: manifest.sources.length,
      timestamp: new Date().toISOString(),
    });
  };

  if (!manifest) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-400">No workspace manifest loaded</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Bundle Assembly</h2>
          <button
            onClick={handleAssembleBundle}
            className="px-4 py-2 bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/50 rounded hover:bg-[#00d4aa]/30 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Assemble Bundle
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400">Sections</p>
            <p className="text-2xl font-bold text-white">{manifest.sections.length}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400">Claims</p>
            <p className="text-2xl font-bold text-white">{manifest.claims.length}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400">Sources</p>
            <p className="text-2xl font-bold text-white">{manifest.sources.length}</p>
          </div>
        </div>

        {bundleStatus && (
          <div className={`p-4 rounded-lg ${bundleStatus.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            {bundleStatus.success ? (
              <div>
                <p className="text-green-400 font-medium mb-2">✓ Bundle assembled successfully</p>
                <div className="text-sm text-gray-300 space-y-1">
                  <p>Bundle ID: {bundleStatus.bundleId}</p>
                  <p>Sections included: {bundleStatus.sections}</p>
                  <p>Claims included: {bundleStatus.claims}</p>
                  <p>Sources included: {bundleStatus.sources}</p>
                </div>
              </div>
            ) : (
              <p className="text-red-400">✗ {bundleStatus.error}</p>
            )}
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Assembly Rules</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <span className="text-white">Required Sections</span>
            <span className="text-sm text-gray-400">{manifest.assembly.requiredSections.join(', ')}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <span className="text-white">Require Claim Sources</span>
            <Badge status={manifest.assembly.validation.requireClaimSources ? 'success' : 'neutral'} />
          </div>
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <span className="text-white">Require Source Accessibility</span>
            <Badge status={manifest.assembly.validation.requireSourceAccessibility ? 'success' : 'neutral'} />
          </div>
        </div>
      </Card>
    </div>
  );
}

function LinkageView({ manifest }: { manifest: PaperWorkspaceManifest | null }) {
  const [linkageResults, setLinkageResults] = useState<any>(null);

  const handleAnalyzeLinkage = () => {
    if (!manifest) return;
    // Mock linkage analysis result - in production this would call analyzeLinkage and generateLinkageReport
    const linkedClaims = manifest.claims.filter(c => c.sourceIds.length > 0).length;
    const unlinkedClaims = manifest.claims.length - linkedClaims;
    
    setLinkageResults({
      valid: unlinkedClaims === 0,
      totalClaims: manifest.claims.length,
      linkedClaims,
      unlinkedClaims,
      report: {
        timestamp: new Date().toISOString(),
        paperId: manifest.paper.paperId,
        claims: manifest.claims.map(c => ({
          claimId: c.claimId,
          statement: c.statement,
          sourceIds: c.sourceIds,
          linked: c.sourceIds.length > 0,
        })),
      },
    });
  };

  if (!manifest) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-400">No workspace manifest loaded</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Linkage Analysis</h2>
          <button
            onClick={handleAnalyzeLinkage}
            className="px-4 py-2 bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/50 rounded hover:bg-[#00d4aa]/30 transition-colors flex items-center gap-2"
          >
            <GitBranch className="w-4 h-4" />
            Analyze Linkage
          </button>
        </div>

        {linkageResults ? (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${linkageResults.valid ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              {linkageResults.valid ? (
                <p className="text-green-400 font-medium">✓ All claims have valid source linkages</p>
              ) : (
                <p className="text-red-400 font-medium">✗ Some claims lack source linkages</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-gray-400">Total Claims</p>
                <p className="text-2xl font-bold text-white">{linkageResults.totalClaims}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-gray-400">Linked</p>
                <p className="text-2xl font-bold text-green-400">{linkageResults.linkedClaims}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-gray-400">Unlinked</p>
                <p className="text-2xl font-bold text-red-400">{linkageResults.unlinkedClaims}</p>
              </div>
            </div>

            {linkageResults.report && (
              <div className="p-4 bg-black/30 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Linkage Report</p>
                <pre className="text-xs text-gray-300 overflow-auto">{JSON.stringify(linkageResults.report, null, 2)}</pre>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">Click "Analyze Linkage" to validate claim-source connections</p>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Claim-Source Linkages</h3>
        <div className="space-y-2">
          {manifest.claims.map((claim) => (
            <div key={claim.claimId} className="p-3 bg-white/5 rounded-lg">
              <p className="text-white mb-2">{claim.statement}</p>
              <div className="flex items-center gap-2">
                <Badge status={claim.sourceIds.length > 0 ? 'success' : 'error'} />
                <span className="text-xs text-gray-400">
                  {claim.sourceIds.length > 0 ? `${claim.sourceIds.length} source(s)` : 'No sources linked'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function AuthorityView() {
  const [registry, setRegistry] = useState<any>(null);

  useEffect(() => {
    // Mock authority registry - in production this would call createAuthorityRegistry
    setRegistry({
      roles: ['author', 'reviewer', 'editor', 'authority'],
      approvalThreshold: 2,
      permissions: {
        author: ['create', 'edit'],
        reviewer: ['review', 'comment'],
        editor: ['approve', 'reject'],
        authority: ['merge', 'sign'],
      },
    });
  }, []);

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Authority Registry</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400">Defined Roles</p>
            <p className="text-2xl font-bold text-white">{registry?.roles.length || 0}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400">Approval Threshold</p>
            <p className="text-2xl font-bold text-white">{registry?.approvalThreshold || 0}</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold mb-4">Role Definitions</h3>
        <div className="space-y-3">
          {registry?.roles.map((role: string) => (
            <div key={role} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-[#00d4aa]" />
                <span className="text-white capitalize">{role}</span>
              </div>
              <Badge status="info" />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Authority Configuration</h3>
        <div className="space-y-3">
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Default Roles</p>
            <p className="text-white text-sm">author, reviewer, editor, authority</p>
          </div>
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Approval Mode</p>
            <p className="text-white text-sm">Multi-signature with threshold validation</p>
          </div>
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Signature Algorithm</p>
            <p className="text-white text-sm">ed25519 (default)</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function MergeView() {
  const [conflicts, setConflicts] = useState<any[]>([]);

  useEffect(() => {
    // Mock conflicts for demonstration - in production this would use createMergeEngine
    setConflicts([
      {
        id: 'conflict-1',
        type: 'content',
        section: 'introduction',
        description: 'Conflicting changes in introduction paragraph 2',
        status: 'pending',
      },
      {
        id: 'conflict-2',
        type: 'claim',
        claimId: 'claim-1',
        description: 'Claim confidence level mismatch',
        status: 'pending',
      },
    ]);
  }, []);

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Merge Engine</h2>
          <button className="px-4 py-2 bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/50 rounded hover:bg-[#00d4aa]/30 transition-colors flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Check for Conflicts
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400">Pending Conflicts</p>
            <p className="text-2xl font-bold text-orange-400">{conflicts.length}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400">Merge Status</p>
            <p className="text-2xl font-bold text-white">{conflicts.length > 0 ? 'Blocked' : 'Ready'}</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold mb-4">Merge Conflicts</h3>
        {conflicts.length > 0 ? (
          <div className="space-y-3">
            {conflicts.map((conflict) => (
              <div key={conflict.id} className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{conflict.type.toUpperCase()}</span>
                  <Badge status={conflict.status} />
                </div>
                <p className="text-sm text-gray-300 mb-2">{conflict.description}</p>
                <div className="flex gap-2">
                  <button className="px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded text-sm hover:bg-green-500/30">
                    Accept
                  </button>
                  <button className="px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded text-sm hover:bg-red-500/30">
                    Reject
                  </button>
                  <button className="px-3 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-sm hover:bg-blue-500/30">
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No merge conflicts detected</p>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Merge Configuration</h3>
        <div className="space-y-3">
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Conflict Resolution Strategy</p>
            <p className="text-white text-sm">Manual review required for all conflicts</p>
          </div>
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Auto-Merge Rules</p>
            <p className="text-white text-sm">Non-conflicting sections auto-merged</p>
          </div>
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Signature Requirements</p>
            <p className="text-white text-sm">All merges require authority approval</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function ReceiptsView({ receipts }: { receipts: RevisionReceipt[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Authority Receipts</h2>
        <span className="text-sm text-gray-400">{receipts.length} receipts</span>
      </div>
      
      {receipts.map((receipt) => (
        <Card key={receipt.receiptId} className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Revision {receipt.revision}</h3>
              <p className="text-sm text-gray-400">{receipt.revisionMetadata.author}</p>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <Badge status="success" />
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-gray-300">{new Date(receipt.revisionMetadata.timestamp).toLocaleString()}</span>
            </div>
            
            <div className="p-3 bg-black/30 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-300">{receipt.revisionMetadata.description}</p>
            </div>
            
            <div className="p-3 bg-black/30 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Signature</p>
              <code className="text-xs text-cyan-400 font-mono">{receipt.signature.signature}</code>
            </div>
            
            <div className="p-3 bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Receipt Details</p>
              <div className="space-y-1 text-xs text-gray-300">
                <p>Receipt ID: {receipt.receiptId}</p>
                <p>Manifest Hash: {receipt.manifestHash}</p>
                <p>Created by: {receipt.createdBy}</p>
              </div>
            </div>
          </div>
        </Card>
      ))}
      
      {receipts.length === 0 && (
        <Card className="p-8 text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400">No authority receipts found</p>
        </Card>
      )}
    </div>
  );
}

